import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useDueRecurringSchedules } from '@/hooks/useRecurringSchedules';
import { FilterDrawer } from './components/filters/FilterDrawer';
import { FilterProfileManagerModal } from './components/filters/FilterProfileManagerModal';
import { JobListToolbar } from './components/JobListToolbar';
import { JobListTable } from './components/JobListTable';
import { JobListCards } from './components/JobListCards';
import { useJobListFilters } from './hooks/useJobListFilters';

export default function JobList() {
  const navigate = useNavigate();
  const [highlightFilter, setHighlightFilter] = useState<string | null>(null);
  const {
    jobs,
    isLoading,
    isFetching,
    error,
    filters,
    availableClients,
    isDrawerOpen,
    isAnimating,
    selectedPhase,
    selectedStatuses,
    selectedJobCardStatuses,
    selectedInvoiceStatuses,
    selectedClients,
    selectedProjectTypes,
    searchText,
    showArchived,
    showDeleted,
    dateField,
    dateFrom,
    dateTo,
    datePreset,
    filterProfiles,
    activeProfileName,
    isProfileModified,
    isProfileModalOpen,
    setIsProfileModalOpen,
    openDrawer,
    closeDrawer,
    handleSort,
    applyFilters,
    clearFilters,
    handlePhaseChange,
    handleSearchChange,
    handleArchivedChange,
    handleDateFilterChange,
    handleDatePreset,
    clearDateFilter,
    handleProfileChange,
    resetToProfile,
    saveProfile,
    updateProfile,
    deleteProfile,
    setDefaultProfile,
    setSelectedStatuses,
    setSelectedJobCardStatuses,
    setSelectedInvoiceStatuses,
    setSelectedClients,
    setSelectedProjectTypes,
  } = useJobListFilters();

  const { data: dueSchedules } = useDueRecurringSchedules();
  const dueCount = dueSchedules?.length ?? 0;

  const handleRowClick = (jobId: string) => {
    navigate(`/jobs/${jobId}`, { state: { fromJobList: true } });
  };

  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    if (highlightFilter) {
      return jobs.filter(job => job.highlightColor === highlightFilter);
    }
    return jobs;
  }, [jobs, highlightFilter]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">
          Error loading jobs: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      {dueCount > 0 && (
        <Link
          to="/recurring"
          className="block mb-4 px-4 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 hover:bg-amber-100 transition-colors"
        >
          {dueCount === 1
            ? '1 recurring job is due — Review'
            : `${dueCount} recurring jobs are due — Review`}
        </Link>
      )}

      <JobListToolbar
        filterProfiles={filterProfiles}
        activeProfileName={activeProfileName}
        isProfileModified={isProfileModified}
        onProfileChange={handleProfileChange}
        onClearFilters={clearFilters}
        onResetToProfile={resetToProfile}
        onOpenDrawer={openDrawer}
        selectedPhase={selectedPhase}
        onPhaseChange={handlePhaseChange}
        showArchived={showArchived}
        showDeleted={showDeleted}
        onArchivedChange={handleArchivedChange}
        searchText={searchText}
        onSearchChange={handleSearchChange}
        isFetching={isFetching}
        isLoading={isLoading}
        highlightFilter={highlightFilter}
        onHighlightFilterChange={setHighlightFilter}
        sortField={filters.sortField}
        sortDirection={filters.sortDirection}
        onSort={handleSort}
      />

      <JobListTable
        jobs={filteredJobs}
        selectedPhase={selectedPhase}
        sortField={filters.sortField}
        sortDirection={filters.sortDirection}
        onSort={handleSort}
        onRowClick={handleRowClick}
      />

      <JobListCards
        jobs={filteredJobs}
        selectedPhase={selectedPhase}
        onRowClick={handleRowClick}
      />

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        isAnimating={isAnimating}
        onClose={closeDrawer}
        onApply={applyFilters}
        onClear={clearFilters}
        onManageProfiles={() => setIsProfileModalOpen(true)}
        showArchived={showArchived}
        showDeleted={showDeleted}
        selectedStatuses={selectedStatuses}
        onStatusesChange={setSelectedStatuses}
        selectedJobCardStatuses={selectedJobCardStatuses}
        onJobCardStatusesChange={setSelectedJobCardStatuses}
        selectedInvoiceStatuses={selectedInvoiceStatuses}
        onInvoiceStatusesChange={setSelectedInvoiceStatuses}
        selectedClients={selectedClients}
        onClientsChange={setSelectedClients}
        selectedProjectTypes={selectedProjectTypes}
        onProjectTypesChange={setSelectedProjectTypes}
        availableClients={availableClients}
        searchText={searchText}
        dateField={dateField}
        dateFrom={dateFrom}
        dateTo={dateTo}
        datePreset={datePreset}
        onDateFilterChange={handleDateFilterChange}
        onDatePreset={handleDatePreset}
        onClearDateFilter={clearDateFilter}
      />
      <FilterProfileManagerModal
        open={isProfileModalOpen}
        onOpenChange={setIsProfileModalOpen}
        profiles={filterProfiles}
        activeProfileName={activeProfileName}
        onSaveProfile={saveProfile}
        onUpdateProfile={updateProfile}
        onDeleteProfile={deleteProfile}
        onSetDefaultProfile={setDefaultProfile}
      />
    </div>
  );
}
